/**
 * ZuoraIdentityReviews — the operator surface for the Zuora identity-review queue.
 *
 * Each row here is reconciliation work that was deliberately REFUSED and held because the
 * resolver landed on a contact contradicting the account the event was for. Nothing
 * releases it except a human saying what was done. Before this existed the queue had no
 * read path at all, so the reviews simply accumulated.
 *
 * The design goal is that an operator can tell WHAT the contradiction is without opening
 * Zuora in another tab: the CRM's value and Zuora's value are shown side by side with the
 * conflict named, and the count of held rows makes the cost of leaving it visible.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import {
  REVIEW_PAGE_SIZE,
  RESOLUTION_ACTIONS,
  type IdentityReview,
  type ResolutionAction,
  getIdentityReviewCounts,
  listIdentityReviews,
  resolveIdentityReview,
} from "../../services/zuoraIdentityReviews.service";

const CONFLICT_COPY: Record<string, string> = {
  different_zuora_account:
    "This contact already belongs to a different Zuora account",
  different_member_number: "This contact carries a different membership number",
  contact_missing: "The matched contact no longer exists",
  // A secondary review is about the account's SECONDARY member, not the primary contact stored
  // on the review row — which is why these used to fall through to the meaningless "other".
  secondary_matched_other_account:
    "This account's secondary member is already linked to a different Zuora account",
  secondary_conflict_contact_not_found:
    "This account's secondary member conflicted with an existing contact that can no longer be found",
  other: "Identity could not be confirmed",
};

const ZuoraIdentityReviews: React.FC = () => {
  const queryClient = useQueryClient();
  const [includeResolved, setIncludeResolved] = useState(false);
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [action, setAction] = useState<ResolutionAction | "">("");
  const [note, setNote] = useState("");
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const counts = useQuery({
    queryKey: ["zuora-identity-review-counts"],
    queryFn: getIdentityReviewCounts,
  });

  const reviews = useQuery({
    queryKey: ["zuora-identity-reviews", includeResolved, page],
    queryFn: () =>
      listIdentityReviews(
        includeResolved,
        REVIEW_PAGE_SIZE,
        page * REVIEW_PAGE_SIZE,
      ),
    // Keep the current page on screen while the next one loads, so paging does not flash empty.
    placeholderData: (prev) => prev,
  });

  const resolve = useMutation({
    mutationFn: (vars: {
      id: string;
      action: ResolutionAction;
      note: string;
    }) => resolveIdentityReview(vars.id, vars.action, vars.note, true),
    onSuccess: (result) => {
      setFailure(null);
      // Report what actually happened to the held work — "resolved" alone does not tell
      // the operator whether anything was released.
      setBanner(
        `Resolved. ${result.requeued_rows ?? 0} reconciliation row(s) returned to the drain` +
          (result.superseded_rows
            ? `, ${result.superseded_rows} duplicate row(s) closed as superseded`
            : "") +
          (result.requeued_events
            ? `, ${result.requeued_events} held webhook event(s) replayed`
            : "") +
          ".",
      );
      setOpenId(null);
      setAction("");
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["zuora-identity-reviews"] });
      queryClient.invalidateQueries({
        queryKey: ["zuora-identity-review-counts"],
      });
    },
    onError: (err: Error) => {
      setBanner(null);
      setFailure(err.message);
    },
  });

  const c = counts.data;

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Identity reviews
          </h2>
          <p className="text-sm text-gray-600">
            Reconciliation that was refused because the contact contradicted the
            account. It stays held until someone says what was done.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => {
              setIncludeResolved(e.target.checked);
              setPage(0);
            }}
          />
          Show resolved
        </label>
      </div>

      {c && (
        <div className="flex flex-wrap gap-3 mb-4 text-sm">
          <span className="px-3 py-1 rounded-full bg-red-50 text-red-800 border border-red-200">
            {c.open_primary} primary
          </span>
          <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200">
            {c.open_secondary} secondary
          </span>
          <span className="px-3 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
            {c.held_rows} reconciliation row(s) held
          </span>
        </div>
      )}

      {banner && (
        <div
          role="status"
          className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {banner}
        </div>
      )}
      {failure && (
        <div
          role="alert"
          className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {failure}
        </div>
      )}
      {reviews.error && (
        <div
          role="alert"
          className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {(reviews.error as Error).message}
        </div>
      )}

      {reviews.isLoading ? (
        <div className="text-sm text-gray-500 py-6">Loading reviews…</div>
      ) : (reviews.data ?? []).length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          No {includeResolved ? "" : "open "}identity reviews. Nothing is being
          held.
        </div>
      ) : (
        <div className="space-y-3">
          {(reviews.data ?? []).map((r: IdentityReview) => (
            <div
              key={r.id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon
                      className={`h-5 w-5 ${
                        r.reason === "primary_matched_different_account"
                          ? "text-red-500"
                          : "text-amber-500"
                      }`}
                    />
                    <span className="font-medium text-gray-900">
                      {CONFLICT_COPY[r.conflict_kind] ?? CONFLICT_COPY.other}
                    </span>
                    {r.resolved_at && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-800 border border-green-200">
                        resolved
                      </span>
                    )}
                  </div>

                  {/* Side by side, so the operator does not have to open Zuora to see
                      which two values disagree. */}
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">
                        Zuora account
                      </dt>
                      <dd className="font-mono text-xs text-gray-900 break-all">
                        {r.zuora_account_id ?? "—"}
                        {r.account_number ? ` (${r.account_number})` : ""}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">
                        Contact holds
                      </dt>
                      <dd className="font-mono text-xs text-gray-900 break-all">
                        {r.contact_zuora_account_id ?? "— none —"}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">
                        Account member #
                      </dt>
                      <dd className="font-mono text-xs text-gray-900 break-all">
                        {r.account_member_number ?? "—"}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">
                        Contact membership
                      </dt>
                      <dd className="font-mono text-xs text-gray-900 break-all">
                        {r.contact_membership_id ?? "— none —"}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">Contact</dt>
                      <dd className="text-gray-900 break-all">
                        {r.primary_contact_id ? (
                          <a
                            className="text-primary-600 hover:underline"
                            href={`/contacts/${r.primary_contact_id}`}
                          >
                            {r.contact_name ||
                              r.contact_email ||
                              r.primary_contact_id}
                          </a>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                    {r.conflicting_contact_id && (
                      <>
                        <div className="flex gap-2">
                          <dt className="text-gray-500 w-40 shrink-0">
                            Conflicting contact
                          </dt>
                          <dd className="text-gray-900 break-all">
                            <a
                              className="text-primary-600 hover:underline"
                              href={`/contacts/${r.conflicting_contact_id}`}
                            >
                              {r.conflicting_contact_name ||
                                r.conflicting_contact_email ||
                                r.conflicting_contact_id}
                            </a>
                            {r.conflicting_contact_account && (
                              <span className="text-gray-500">
                                {" "}
                                on {r.conflicting_contact_account}
                              </span>
                            )}
                          </dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500 w-40 shrink-0">
                            Matched on
                          </dt>
                          <dd className="font-mono text-xs text-gray-900 break-all">
                            {r.secondary_member_number ?? "—"}
                            {r.secondary_email ? ` / ${r.secondary_email}` : ""}
                          </dd>
                        </div>
                      </>
                    )}
                    <div className="flex gap-2">
                      <dt className="text-gray-500 w-40 shrink-0">Seen</dt>
                      <dd className="text-gray-900">
                        {r.occurrences ?? 1}× since{" "}
                        {r.first_seen_at
                          ? new Date(r.first_seen_at).toLocaleDateString()
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="text-right shrink-0">
                  <div
                    className={`text-sm ${
                      r.held_queue_rows > 0
                        ? "text-red-700 font-medium"
                        : "text-gray-500"
                    }`}
                  >
                    {r.held_queue_rows} row(s) held
                  </div>
                  {!r.resolved_at && (
                    <Button
                      variant="secondary"
                      className="mt-2"
                      onClick={() => {
                        setOpenId(openId === r.id ? null : r.id);
                        setAction("");
                        setNote("");
                        setFailure(null);
                      }}
                    >
                      {openId === r.id ? "Cancel" : "Resolve"}
                    </Button>
                  )}
                </div>
              </div>

              {openId === r.id && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What was actually done?
                  </label>
                  <select
                    value={action}
                    onChange={(e) =>
                      setAction(e.target.value as ResolutionAction | "")
                    }
                    className="w-full sm:w-96 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">— Choose —</option>
                    {RESOLUTION_ACTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  {action && (
                    <p className="mt-1 text-xs text-gray-500 max-w-xl">
                      {RESOLUTION_ACTIONS.find((a) => a.value === action)?.help}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500 max-w-xl">
                    Resolving releases the {r.held_queue_rows} held row(s) back
                    to the drain. If the underlying conflict has not actually
                    been fixed, the next drain will simply re-open this review.
                  </p>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="Note (optional, max 500 characters)"
                    className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="mt-3">
                    <Button
                      disabled={!action || resolve.isPending}
                      onClick={() =>
                        action &&
                        resolve.mutate({
                          id: r.id,
                          action: action as ResolutionAction,
                          note,
                        })
                      }
                    >
                      {resolve.isPending ? "Resolving…" : "Resolve and release"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Paging. The previous version asked for a fixed 200 rows at offset 0, so on PROD 57 of 257
          reviews were simply invisible — a work queue that hides work looks finished when it is not. */}
      {(page > 0 || (reviews.data ?? []).length === REVIEW_PAGE_SIZE) && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="secondary"
            disabled={page === 0 || reviews.isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Previous
          </Button>
          <span className="text-sm text-gray-600">
            Showing {page * REVIEW_PAGE_SIZE + 1}–
            {page * REVIEW_PAGE_SIZE + (reviews.data ?? []).length}
            {c
              ? ` of ${includeResolved ? c.open_total + c.resolved_total : c.open_total}`
              : ""}
          </span>
          <Button
            variant="secondary"
            // A full page means there is probably another; the count above is the authority.
            disabled={
              (reviews.data ?? []).length < REVIEW_PAGE_SIZE ||
              reviews.isFetching
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
};

export default ZuoraIdentityReviews;
